(() => {
  'use strict';
  function init(root) {
    if (root.dataset.teamBound) return;
    root.dataset.teamBound = 'true';
    const one = (selector) => root.querySelector(selector);
    const catalogue = one('[data-team-catalogue]');
    const status = one('[data-team-catalogue-status]');
    const filters = one('[data-team-filters]');
    const brandSelect = filters.elements['filter.p.vendor'];
    const typeSelect = filters.elements['filter.p.product_type'];
    const brands = Array.from(brandSelect.options).map((o) => o.value).filter(Boolean);
    const isCatalogue = root.dataset.catalogueOnly === 'true';
    const minimum = Number(root.dataset.minimum) || 4;
    const money = (cents) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: root.dataset.currency || 'GBP' }).format(cents / 100);
    const selected = new Map();
    const states = new WeakMap();
    let busy = false;
    let uncertain = false;
    let basketEligible = 0;
    let selectionVisible = false;
    let catalogueVersion = 0;
    let controller;
    let lastURL;
    const initial = new URL(location.href);
    if (brands.includes(initial.searchParams.get('filter.p.vendor'))) brandSelect.value = initial.searchParams.get('filter.p.vendor');
    const initialType = initial.searchParams.getAll('filter.p.product_type').join('\u001f');
    const text = (tag, content, className) => {
      const element = document.createElement(tag);
      element.textContent = content;
      if (className) element.className = className;
      return element;
    };
    async function getHTML(url, signal) {
      const response = await fetch(url, { credentials: 'same-origin', signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Could not load the current product information.');
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }
    function catalogueURL(pageURL) {
      const url = new URL(pageURL || root.dataset.catalogueUrl, location.origin);
      if (url.origin !== location.origin || url.pathname !== root.dataset.catalogueUrl) throw new Error('Invalid catalogue address.');
      url.searchParams.delete('view');
      url.searchParams.delete('filter.p.vendor');
      (brandSelect.value ? [brandSelect.value] : brands).forEach((brand) => url.searchParams.append('filter.p.vendor', brand));
      url.searchParams.delete('filter.p.product_type');
      if (typeSelect.value) typeSelect.value.split('\u001f').forEach((type) => url.searchParams.append('filter.p.product_type', type));
      url.searchParams.set('section_id', 'ffc-team-catalogue');
      return url;
    }
    async function loadCatalogue(pageURL) {
      const version = ++catalogueVersion;
      if (controller) controller.abort();
      controller = new AbortController();
      catalogue.setAttribute('aria-busy', 'true');
      status.textContent = 'Loading workwear…';
      one('[data-team-retry]').hidden = true;
      // Do not allow stale results to remain interactive under newly selected filters.
      catalogue.replaceChildren();
      try {
        lastURL = catalogueURL(pageURL);
        const document_ = await getHTML(lastURL, controller.signal);
        if (version !== catalogueVersion) return;
        const result = document_.querySelector('[data-team-catalogue-response]');
        if (!result) throw new Error('The workwear catalogue is unavailable.');
        const types = JSON.parse(result.querySelector('[data-team-types]').textContent);
        const keepType = typeSelect.value || (catalogueVersion === 1 ? initialType : '');
        typeSelect.replaceChildren(new Option('All workwear types', ''));
        types.filter(Boolean).forEach((type) => typeSelect.add(new Option(type, type)));
        if (keepType.includes('\u001f')) typeSelect.add(new Option(keepType.split('\u001f').join(' & '), keepType));
        if (Array.from(typeSelect.options).some((o) => o.value === keepType)) typeSelect.value = keepType;
        catalogue.replaceChildren(result);
        const cards = catalogue.querySelectorAll('[data-team-product]');
        status.textContent = cards.length ? 'Choose from ' + cards.length + (cards.length === 1 ? ' product' : ' products') + ' on this page. Only the selected workwear brands are shown.' : 'No workwear products match these filters.';
        cards.forEach(bindProduct);
        catalogue.querySelectorAll('[data-team-page]').forEach((link) => link.addEventListener('click', (event) => {
          event.preventDefault();
          loadCatalogue(link.href);
        }));
      } catch (error) {
        if (error.name === 'AbortError' || version !== catalogueVersion) return;
        status.textContent = 'Workwear could not be loaded. Please try again.';
        one('[data-team-retry]').hidden = false;
      } finally {
        if (version === catalogueVersion) catalogue.setAttribute('aria-busy', 'false');
      }
    }
    async function lookup(url) {
      const parsed = new URL(url, location.origin);
      if (parsed.origin !== location.origin || !parsed.pathname.includes('/products/')) throw new Error('Invalid product address.');
      parsed.searchParams.set('section_id', 'ffc-team-variant');
      const document_ = await getHTML(parsed);
      const script = document_.querySelector('[data-team-variant]');
      const variant = script ? JSON.parse(script.textContent) : null;
      if (!variant || !brands.includes(variant.vendor)) throw new Error('This option is not available in the workwear range.');
      return { ...variant, url: parsed.href };
    }
    function validQuantity(variant, qty) {
      return Number.isSafeInteger(qty) && qty >= variant.minimum && qty % variant.increment === 0 && (!variant.maximum || qty <= variant.maximum);
    }
    function availableFor(variant, qty) {
      // Match the ordinary configurator: tracked stock only, with its existing 10% buffer.
      return variant.available && variant.tracked && validQuantity(variant, qty) && qty <= Math.max(0, Math.floor(variant.quantity * 0.9));
    }
    async function readEligibility() {
      const response = await fetch(root.dataset.cartUrl + '?view=ffc-personalisation-data', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('The current personalisation eligibility could not be checked.');
      const data = await response.json();
      if (data.version !== 1 || !Number.isSafeInteger(data.eligibleQuantity) || data.eligibleQuantity < 0) throw new Error('The basket eligibility could not be verified.');
      return data.eligibleQuantity;
    }
    async function refreshEligibility() {
      if (isCatalogue || busy || uncertain) return;
      try { basketEligible = await readEligibility(); } catch (_) { basketEligible = 0; }
      renderSelection();
    }
    function bindProduct(card) {
      const details = card.querySelector('[data-team-configure]');
      if (isCatalogue) { details.remove(); return; }
      const form = card.querySelector('[data-team-product-form]');
      const selects = Array.from(form.querySelectorAll('[data-team-option]'));
      const note = form.querySelector('[data-team-variant-status]');
      const button = form.querySelector('button');
      const quantity = form.querySelector('[data-team-quantity]');
      const state = { variant: null, version: 0 };
      states.set(card, state);
      function refresh() {
        const variant = state.variant;
        const qty = Number(quantity.value);
        const already = variant && selected.get(String(variant.id));
        const combined = qty + (already ? already.qty : 0);
        button.disabled = busy || uncertain || !variant || !availableFor(variant, combined);
        if (!variant) return;
        if (!variant.available) note.textContent = 'This colour and size is currently unavailable.';
        else if (!validQuantity(variant, combined)) note.textContent = 'Check the quantity required for this product.';
        else if (!availableFor(variant, combined)) note.textContent = 'Your combined selection exceeds the available stock.';
        else note.textContent = money(variant.price) + ' each · available';
      }
      async function choose() {
        const version = ++state.version;
        state.variant = null;
        button.disabled = true;
        if (selects.some((select) => !select.value)) { note.textContent = 'Choose all options to check availability.'; return; }
        const url = new URL(card.dataset.productUrl, location.origin);
        url.searchParams.set('option_values', selects.map((select) => select.value).join(','));
        note.textContent = 'Checking availability…';
        try {
          const variant = await lookup(url);
          if (version !== state.version) return;
          if (String(variant.productId) !== card.dataset.productId) throw new Error('The product could not be verified.');
          state.variant = variant;
          quantity.min = String(variant.minimum);
          quantity.step = String(variant.increment);
          if (variant.maximum) quantity.max = String(variant.maximum); else quantity.removeAttribute('max');
          const image = card.querySelector('img');
          if (image && variant.image) { image.src = variant.image; image.removeAttribute('srcset'); image.alt = variant.title + ' — ' + variant.options.join(' / '); }
          refresh();
        } catch (error) {
          if (version !== state.version) return;
          note.textContent = error.message + ' Change an option to try again.';
        }
      }
      selects.forEach((select) => select.addEventListener('change', choose));
      quantity.addEventListener('input', refresh);
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (busy || uncertain || !form.reportValidity() || button.disabled || !state.variant) return;
        const key = String(state.variant.id);
        const old = selected.get(key);
        const qty = Number(quantity.value) + (old ? old.qty : 0);
        if (!availableFor(state.variant, qty)) return;
        selected.set(key, { ...state.variant, qty });
        renderSelection();
        note.textContent = 'Added to your selection. Choose another colour or size, or continue to the basket.';
        quantity.value = '1';
        refresh();
      });
    }
    function renderSelection() {
      if (isCatalogue) return;
      one('.ffc-team-browser').inert = busy || uncertain;
      const lines = one('[data-team-lines]');
      lines.replaceChildren();
      let count = 0, total = 0, eligible = 0;
      selected.forEach((line, key) => {
        count += line.qty; total += line.price * line.qty;
        if (line.personalisable) eligible += line.qty;
        const li = text('li', '', 'ffc-team-line');
        if (line.image) { const img = document.createElement('img'); img.src = line.image; img.alt = ''; img.width = 64; img.height = 72; li.append(img); }
        const body = text('div', '', 'ffc-team-line__body');
        body.append(text('strong', line.title), text('span', line.options.join(' / ')), text('small', line.personalisable ? 'Personalisation available' : 'Blank only'));
        const label = text('label', 'Quantity');
        const input = document.createElement('input');
        input.type = 'number'; input.min = String(line.minimum); input.step = String(line.increment); input.value = String(line.qty); input.inputMode = 'numeric'; input.disabled = busy || uncertain;
        input.setAttribute('aria-label', 'Quantity for ' + line.title + ' — ' + line.options.join(' / '));
        if (line.maximum) input.max = String(line.maximum);
        input.addEventListener('change', () => {
          const qty = Number(input.value);
          if (!availableFor(line, qty)) { input.setCustomValidity('Choose a valid quantity within available stock.'); input.reportValidity(); input.value = String(line.qty); input.setCustomValidity(''); return; }
          line.qty = qty; renderSelection();
        });
        label.append(input); body.append(label, text('b', money(line.qty * line.price)));
        const remove = text('button', 'Remove'); remove.type = 'button'; remove.disabled = busy || uncertain;
        remove.setAttribute('aria-label', 'Remove ' + line.title + ' — ' + line.options.join(' / '));
        remove.addEventListener('click', () => { selected.delete(key); renderSelection(); one('[data-team-count]').closest('h2').setAttribute('tabindex', '-1'); one('[data-team-count]').closest('h2').focus(); });
        body.append(remove); li.append(body); lines.append(li);
      });
      one('[data-team-empty]').hidden = selected.size > 0;
      one('[data-team-count]').textContent = String(count);
      one('[data-team-total]').textContent = money(total);
      const ready = eligible > 0 && eligible + basketEligible >= minimum;
      const progress = eligible + ' eligible garments selected' + (basketEligible ? ' + ' + basketEligible + ' already in the basket' : '');
      one('[data-team-personalisation]').textContent = ready ? progress + '. Choose your personalisation in the basket.' : progress + '. Personalisation from ' + minimum + ' eligible garments.';
      root.querySelectorAll('[data-team-add]').forEach((button) => { button.disabled = busy || uncertain || !selected.size || (button.dataset.teamAdd === 'personalise' && !ready); });
      updateDock();
    }
    function updateDock() {
      if (isCatalogue) return;
      const dock = one('[data-team-dock]');
      dock.hidden = selected.size === 0 || selectionVisible;
      const lines = Array.from(selected.values());
      dock.textContent = 'View selection · ' + lines.reduce((sum, line) => sum + line.qty, 0) + ' items · ' + money(lines.reduce((sum, line) => sum + line.qty * line.price, 0));
    }
    async function submit(mode) {
      if (busy || uncertain || !selected.size) return;
      busy = true; renderSelection();
      const result = one('[data-team-result]'); result.hidden = false; result.textContent = 'Checking your selection and current stock…';
      let submitted = false;
      try {
        const cartResponse = await fetch(root.dataset.cartUrl + '.js', { credentials: 'same-origin', cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('The basket could not be checked. No products have been submitted.');
        const basket = await cartResponse.json();
        const fresh = await Promise.all(Array.from(selected.values()).map(async (line) => {
          const current = await lookup(line.url);
          const inBasket = basket.items.filter((item) => String(item.variant_id || item.id) === String(current.id)).reduce((sum, item) => sum + item.quantity, 0);
          if (String(current.id) !== String(line.id) || current.price !== line.price || current.personalisable !== line.personalisable) throw new Error('A product has changed. Reload its options and review your selection before adding it.');
          if (!validQuantity(current, line.qty) || !availableFor(current, line.qty + inBasket)) throw new Error('Stock or quantity limits have changed for ' + line.title + '. Review your selection.');
          return { ...current, qty: line.qty };
        }));
        const eligible = fresh.filter((line) => line.personalisable).reduce((sum, line) => sum + line.qty, 0);
        if (mode === 'personalise') {
          const inBasketEligible = await readEligibility();
          if (!eligible || eligible + inBasketEligible < minimum) throw new Error('Choose at least ' + minimum + ' eligible garments across your selection and basket to personalise this order.');
        }
        const items = fresh.map((line) => ({ id: Number(line.id), quantity: line.qty, properties: mode === 'personalise' && line.personalisable ? { Personalisation: 'Awaiting personalisation (finish in the cart)', _fp_pending: 'yes' } : { Personalisation: 'No personalisation (blank)' } }));
        result.textContent = 'Adding your selection to the basket…';
        submitted = true;
        const response = await fetch(root.dataset.addUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        if (!response.ok) throw new Error('The basket could not confirm the whole selection. Check the basket before adding anything again.');
        const addition = await response.json();
        if (!Array.isArray(addition.items) || addition.items.length !== items.length) throw new Error('Please check the basket to confirm which items were added.');
        selected.clear();
        location.assign(root.dataset.cartUrl);
      } catch (error) {
        // An add failure can be partial or have an unknown outcome. Never retry it automatically.
        uncertain = submitted;
        result.textContent = error.message + (submitted ? ' Open the basket using the link below.' : '');
        result.focus();
      } finally { busy = false; renderSelection(); }
    }
    filters.addEventListener('submit', (event) => { event.preventDefault(); loadCatalogue(); });
    one('[data-team-retry]').addEventListener('click', () => loadCatalogue(lastURL));
    root.querySelectorAll('[data-team-add]').forEach((button) => button.addEventListener('click', () => submit(button.dataset.teamAdd)));
    // Apply a type link from the homepage before the first request.
    if (initialType) { typeSelect.add(new Option(initialType.split('\u001f').join(' & '), initialType)); typeSelect.value = initialType; }
    loadCatalogue(); renderSelection(); refreshEligibility();
    if (!isCatalogue && 'IntersectionObserver' in window) {
      const summaryObserver = new IntersectionObserver((entries) => {
        selectionVisible = entries[0].isIntersecting;
        updateDock();
      });
      summaryObserver.observe(one('.ffc-team-selection'));
    }
    window.addEventListener('pageshow', refreshEligibility);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshEligibility(); });
  }
  const boot = () => document.querySelectorAll('[data-team-builder]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
